import React, { Fragment, ReactNode, useMemo } from 'react';
import { StyleSheet, TouchableOpacity } from 'react-native';

import FootballIcon from '@/assets/icons/footballIcon';
import ShieldIcon from '@/assets/icons/shield';
import { Text } from '@/components/TextComponent';
import { Box, Image, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { designSystem } from '@/styles/styles';
import { League } from '@/types/api.generated';
import { isCollegeSport, isTennis } from '@/utils/league';
import debounce from 'lodash.debounce';

export type EventCardProps = {
    teamInfo?: [string | ReactNode, string | ReactNode];
    score?: [number, number];
    isLive?: boolean;
    isFinished?: boolean;
    eventTitle: ReactNode | string;
    awayTeamIcon: string;
    homeTeamIcon: string;
    awayTeamIconComponent?: ReactNode;
    homeTeamIconComponent?: ReactNode;
    shortDescription?: string;
    awayTeamShortCode: string;
    homeTeamShortCode: string;
    awayPrimaryTeamColor?: string;
    awaySecondaryTeamColor?: string;
    homePrimaryTeamColor?: string;
    homeSecondaryTeamColor?: string;
    league: League;
    awayScore?: number;
    homeScore?: number;
    variant: 'lobby' | 'eventDetails';
    ballPossession?: 'HOME' | 'AWAY';
    isSelected?: boolean;
    onPress: () => void;
    index?: number;
};

// TODO: check with SBK which sports actually can render and subscribe to live scores
export const EventCard = ({
    awayTeamIcon,
    awayTeamIconComponent,
    awayTeamShortCode,
    homeTeamIcon,
    homeTeamIconComponent,
    homeTeamShortCode,
    teamInfo,
    isLive,
    isFinished,
    eventTitle,
    awayPrimaryTeamColor,
    homePrimaryTeamColor,
    awaySecondaryTeamColor,
    homeSecondaryTeamColor,
    awayScore,
    homeScore,
    league,
    isSelected,
    ballPossession,
    variant,
    shortDescription,
    onPress,
    index,
}: EventCardProps) => {
    // keep scores on it's own since we don't want to re render the entire card when the score changes
    const scores = useMemo(() => {
        if (awayScore === undefined || homeScore === undefined || awayScore === null || homeScore === null) {
            return undefined;
        }
        return [awayScore, homeScore];
    }, [awayScore, homeScore]);

    const teamLoser = useMemo(() => {
        if (isFinished && homeScore !== undefined && awayScore !== undefined) {
            return homeScore > awayScore ? 'AWAY' : homeScore < awayScore ? 'HOME' : undefined;
        }
        return undefined;
    }, [isFinished, homeScore, awayScore]);

    const teamDetails = useMemo(() => {
        return [
            {
                icon: awayTeamIcon,
                iconComponent: awayTeamIconComponent,
                primaryColor: awayPrimaryTeamColor,
                secondaryColor: awaySecondaryTeamColor,
                shortCode: awayTeamShortCode,
                showPossession: ballPossession === 'AWAY',
                isLoser: teamLoser === 'AWAY',
            },
            {
                icon: homeTeamIcon,
                iconComponent: homeTeamIconComponent,
                primaryColor: homePrimaryTeamColor,
                secondaryColor: homeSecondaryTeamColor,
                shortCode: homeTeamShortCode,
                showPossession: ballPossession === 'HOME',
                isLoser: teamLoser === 'HOME',
            },
        ];
    }, [
        awayPrimaryTeamColor,
        awaySecondaryTeamColor,
        awayTeamIcon,
        awayTeamIconComponent,
        awayTeamShortCode,
        ballPossession,
        homePrimaryTeamColor,
        homeSecondaryTeamColor,
        homeTeamIcon,
        homeTeamIconComponent,
        homeTeamShortCode,
        teamLoser,
    ]);

    const shouldHaveFixedScoreWidth = isLive && awayScore !== undefined && homeScore !== undefined;

    // some sports have different max widths for the score
    const scoreWidth = shouldHaveFixedScoreWidth
        ? (leagueToScoreWidth[league] || 18) + SCORE_CONTAINER_PADDING_LEFT
        : 'auto';

    const variantStyle = variantToStyle[variant];

    const onPressWrapper = useMemo(
        () =>
            debounce(
                () => {
                    onPress();
                },
                200,
                { leading: true, trailing: false }
            ),
        [onPress]
    );

    return (
        <TouchableOpacity onPress={onPressWrapper} testID={`event-card-${index}`}>
            <Box
                borderWidth={BORDER_WIDTH}
                borderStyle={'solid'}
                style={[
                    variantStyle.containerStyle,
                    isSelected ? styles.selectedContainer : null,
                    { borderColor: variantStyle.borderColor },
                ]}
            >
                <Text
                    variant={'bodySmaller'}
                    color={'neutral300'}
                    lineHeight={16}
                    letterSpacing={0}
                    style={isLive ? styles.liveTitle : undefined}
                >
                    {eventTitle}
                </Text>
                <Row>
                    <Box gap={variantStyle.rowGap}>
                        {teamDetails.map(team =>
                            team.iconComponent ? (
                                <React.Fragment key={`icons-${team.shortCode}`}>{team.iconComponent}</React.Fragment>
                            ) : (
                                <TeamIcon
                                    key={`icons-${team.shortCode}`}
                                    icon={team.icon}
                                    league={league}
                                    primaryColor={team.primaryColor}
                                    secondaryColor={team.secondaryColor}
                                />
                            )
                        )}
                    </Box>
                    <Box paddingLeft={variantStyle.shortCodePaddingLeft} gap={variantStyle.rowGap}>
                        {teamDetails.map(team => (
                            <Row alignItems={'center'} key={`shortCode-${team.shortCode}`}>
                                <Text
                                    fontSize={13}
                                    fontWeight={600}
                                    lineHeight={20}
                                    letterSpacing={-0.08}
                                    color={team.isLoser ? 'neutral300' : ds.colorToken.text.primary}
                                >
                                    {team.shortCode}
                                </Text>
                                {variant === 'lobby' && isLive ? (
                                    team.showPossession ? (
                                        <Box marginLeft={'s8'}>
                                            <FootballIcon />
                                        </Box>
                                    ) : null
                                ) : null}
                            </Row>
                        ))}
                    </Box>

                    {/* the tennis event card is widened by the event location so push
                    the score/rank column to the card's edge */}
                    {isTennis(league) ? <Box flex={1} /> : null}

                    {isLive || isFinished ? (
                        scores ? (
                            <Box gap={variantStyle.rowGap} paddingLeft={'s12'} width={scoreWidth}>
                                {scores.map((score, scoreIndex) => (
                                    <Text
                                        key={scoreIndex}
                                        fontSize={13}
                                        letterSpacing={-0.08}
                                        color={
                                            isFinished && teamDetails[scoreIndex].isLoser
                                                ? 'neutral300'
                                                : ds.colorToken.text.primary
                                        }
                                        textAlign={'right'}
                                        fontWeight={600}
                                        lineHeight={20}
                                    >
                                        {score}
                                    </Text>
                                ))}
                            </Box>
                        ) : null
                    ) : teamInfo ? (
                        <Box gap={variantStyle.rowGap} paddingLeft={'s12'} width={scoreWidth} alignItems={'flex-end'}>
                            {teamInfo?.map((content, infoIndex) => {
                                if (content === null) {
                                    return <Box key={infoIndex} height={20} />; // match the lineHeight of the text
                                }
                                if (typeof content === 'string') {
                                    return (
                                        <Text
                                            key={infoIndex}
                                            variant={'bodySmall'}
                                            color={'neutral300'}
                                            letterSpacing={-0.08}
                                        >
                                            {content}
                                        </Text>
                                    );
                                }
                                return <Fragment key={infoIndex}>{content}</Fragment>;
                            })}
                        </Box>
                    ) : null}
                </Row>
                {shortDescription ? (
                    <Text variant={'labelMedium'} color={'neutral300'}>
                        {shortDescription}
                    </Text>
                ) : null}
            </Box>
        </TouchableOpacity>
    );
};

const BORDER_WIDTH = 1;

const SCORE_CONTAINER_PADDING_LEFT = 12;

export const TeamIcon = ({
    icon,
    league,
    primaryColor,
    secondaryColor,
    width,
    height,
}: {
    icon: string;
    league: League;
    primaryColor?: string;
    secondaryColor?: string;
    width?: number;
    height?: number;
}) => {
    const iconStyle = useMemo(() => {
        return [styles.teamIcon, { width: width ?? 20, height: height ?? 20 }];
    }, [width, height]);

    if (isCollegeSport(league) || !icon) {
        return (
            <ShieldIcon
                primary={primaryColor || designSystem.colors.neutral300}
                secondary={secondaryColor || designSystem.colors.neutral900}
                width={width ?? 20}
                height={height ?? 20}
            />
        );
    }

    return <Image source={{ uri: icon }} style={iconStyle} resizeMode="contain" />;
};

const leagueToScoreWidth: { [key in League]?: number } = {
    [League.Nba]: 26,
    [League.Euroleague]: 26,
    [League.Wnba]: 26,
    [League.Wcbb]: 26,
    [League.Cbb]: 26,
    [League.Nfl]: 18,
    [League.NflFullSeason]: 18,
    [League.Cfb]: 18,
    [League.Mlb]: 18,
    [League.Nhl]: 18,
};

const styles = StyleSheet.create({
    selectedContainer: {
        backgroundColor: ds.colorValue.background.neutral.subtler,
    },
    teamIcon: {
        width: 20,
        height: 20,
    },

    liveTitle: {
        fontSize: 12,
        color: ds.colorValue.text.danger,
        fontWeight: 600,
    },
    eventBorderRadius: {
        borderRadius: 16,
        borderCurve: 'continuous',
    },
});

const variantToStyle = {
    lobby: {
        containerStyle: {
            backgroundColor: ds.colorValue.background.neutral.subtler,
            paddingHorizontal: 16 - BORDER_WIDTH,
            paddingVertical: 12 - BORDER_WIDTH,
            gap: 6,
            ...styles.eventBorderRadius,
        },
        // used for the "isSelected" state
        borderColor: ds.colorValue.border.subtle,
        rowGap: 's4',
        shortCodePaddingLeft: 's8',
    },
    eventDetails: {
        containerStyle: {
            backgroundColor: ds.colorValue.background.neutral.base,
            paddingHorizontal: 12 - BORDER_WIDTH,
            paddingVertical: 10 - BORDER_WIDTH,
            gap: 4,
            ...styles.eventBorderRadius,
        },
        // used for the "isSelected" state
        borderColor: ds.colorValue.border.subtle,
        rowGap: 's2',
        shortCodePaddingLeft: 's6',
    },
} as const;
